import { useState, useMemo, useCallback, useEffect } from 'react';
import { X, Check, ChevronDown, Package, Bookmark, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

/* ─── Colour-to-CSS mapping ─── */
const COLOUR_MAP = {
  'Black': '#000000',
  'White': '#FFFFFF',
  'Pink': '#F4A4B8',
  ':Pink': '#F4A4B8',
  'Light Pink': '#FFD1DC',
  'Pale Pink': '#FADADD',
  'Candy Pink': '#E4717A',
  'Theatrical Pink': '#F77FBE',
  'Ballet Pink Leather': '#E8B4B8',
  'Blush': '#DE98AB',
  'Rose': '#C67D7D',
  'Coral': '#F08080',
  'Raspberry': '#C4295B',
  'Red': '#CC2936',
  'Burgundy': '#800020',
  'Black Cherry': '#49111C',
  'Terracotta': '#C06040',
  'Salted Caramel': '#C49560',
  'Caramel Blonde': '#C49560',
  'Chocolate': '#5C3317',
  'Brown': '#8B6F47',
  'Light Brown': '#B68D66',
  'Med Brown': '#9A6F48',
  'Wood': '#8B7355',
  'Tan': '#D2B48C',
  'Taupe': '#B8A99A',
  'Sand': '#D4C5A9',
  'Nude': '#E3C9A8',
  'Light Nude': '#F0DFC8',
  'Beige': '#F5E1C8',
  'Natural': '#EEDCC8',
  'Oat': '#E9DDD0',
  'Cream': '#FFFDD0',
  'Milky Coconut': '#F5F0E8',
  'Smooth Vanilla': '#F5E6CC',
  'Blonde': '#F5DEB3',
  'Moonstone': '#BCC5CE',
  'Slate': '#708090',
  'Marl': '#A8A8A8',
  'Navy': '#1A2744',
  'Deep Navy': '#0F1A2E',
  'Royal': '#2B3A8E',
  'Haze Blue': '#99B5C8',
  'Forest Green': '#2D5A27',
  'Olive': '#708238',
  'Soft Violet': '#A68BB5',
  'Clear': 'transparent',
  'clear': 'transparent',
  'Salmon/White': 'split:#FA8072:#FFFFFF',
  'Black/Pacific': 'split:#000000:#008B8B',
  'Black/Peach': 'split:#000000:#FFDAB9',
  'Black/Salmon': 'split:#000000:#FA8072',
  'Leopard': 'pattern:leopard',
  'Multi': 'pattern:multi',
};

/* ─── Colour swatch component ─── */
const ColourSwatch = ({ colour, size = 16, selected = false, className = '' }) => {
  const css = COLOUR_MAP[colour];
  const s = `${size}px`;

  if (!css) {
    // Unknown colour — show a grey dot with first letter
    return (
      <span
        className={`inline-flex items-center justify-center rounded-full border border-[#ddd] bg-[#f0f0f0] text-[8px] text-[#999] flex-shrink-0 ${className}`}
        style={{ width: s, height: s }}
        title={colour}
      >
        {colour.charAt(0)}
      </span>
    );
  }

  // Split colour (e.g. Black/Peach)
  if (css.startsWith('split:')) {
    const [, c1, c2] = css.split(':');
    return (
      <span
        className={`inline-block rounded-full flex-shrink-0 overflow-hidden border ${selected ? 'ring-2 ring-[#000] ring-offset-1' : 'border-[#ddd]'} ${className}`}
        style={{ width: s, height: s }}
        title={colour}
        data-testid={`swatch-${colour}`}
      >
        <span className="flex w-full h-full">
          <span style={{ background: c1, width: '50%', height: '100%' }} />
          <span style={{ background: c2, width: '50%', height: '100%' }} />
        </span>
      </span>
    );
  }

  // Pattern (Leopard, Multi)
  if (css.startsWith('pattern:')) {
    const type = css.split(':')[1];
    const bg = type === 'leopard'
      ? 'radial-gradient(circle at 30% 30%, #3B2507 2px, transparent 2px), radial-gradient(circle at 70% 60%, #3B2507 2px, transparent 2px), radial-gradient(circle at 50% 80%, #3B2507 1.5px, transparent 1.5px), linear-gradient(135deg, #D4A952, #C4913A)'
      : 'conic-gradient(#F44, #F94, #FF4, #4F4, #48F, #C4F, #F44)';
    return (
      <span
        className={`inline-block rounded-full flex-shrink-0 border ${selected ? 'ring-2 ring-[#000] ring-offset-1' : 'border-[#ddd]'} ${className}`}
        style={{ width: s, height: s, background: bg }}
        title={colour}
        data-testid={`swatch-${colour}`}
      />
    );
  }

  // Transparent (Clear)
  if (css === 'transparent') {
    return (
      <span
        className={`inline-block rounded-full flex-shrink-0 border-2 border-dashed border-[#ccc] ${selected ? 'ring-2 ring-[#000] ring-offset-1' : ''} ${className}`}
        style={{ width: s, height: s, background: 'repeating-conic-gradient(#eee 0% 25%, transparent 0% 50%) 50%/6px 6px' }}
        title={colour}
        data-testid={`swatch-${colour}`}
      />
    );
  }

  // Solid colour
  const needsBorder = ['#FFFFFF', '#FFFDD0', '#F5F0E8', '#F5E6CC', '#F0DFC8', '#EEDCC8'].includes(css);
  return (
    <span
      className={`inline-block rounded-full flex-shrink-0 ${needsBorder ? 'border border-[#ddd]' : 'border border-transparent'} ${selected ? 'ring-2 ring-[#000] ring-offset-1' : ''} ${className}`}
      style={{ width: s, height: s, backgroundColor: css }}
      title={colour}
      data-testid={`swatch-${colour}`}
    />
  );
};

/* ─── Utility: does a product have ANY size truly in stock? ─── */
const isProductInStock = (product) => {
  const sbs = product.stock_by_size;
  if (!sbs) return product.stock > 0;
  return Object.values(sbs).some(v => v > 0);
};

/* ─── Multi-select dropdown with counts ─── */
const FilterDropdown = ({ label, options, selected, onChange, testIdPrefix, showSwatches = false }) => {
  const [open, setOpen] = useState(false);

  const toggle = (id) => {
    const next = selected.includes(id)
      ? selected.filter(s => s !== id)
      : [...selected, id];
    onChange(next);
  };

  return (
    <div className="relative" data-testid={`${testIdPrefix}-dropdown`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2.5 border border-[#E5D8D0] bg-white text-sm hover:border-[#999] transition-colors"
        data-testid={`${testIdPrefix}-trigger`}
        aria-label={`Filter by ${label}`}
      >
        <span className="flex items-center gap-2">
          {showSwatches && selected.length > 0 && (
            <span className="flex items-center -space-x-1">
              {selected.slice(0, 4).map(c => <ColourSwatch key={c} colour={c} size={14} className="ring-1 ring-white" />)}
              {selected.length > 4 && <span className="text-[10px] text-[#999] pl-1.5">+{selected.length - 4}</span>}
            </span>
          )}
          <span className={selected.length ? 'text-[#000]' : 'text-[#999]'}>
            {selected.length ? `${label} (${selected.length})` : label}
          </span>
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-[#737373] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 right-0 top-full mt-1 z-20 bg-white border border-[#E5D8D0] shadow-lg max-h-60 overflow-y-auto" data-testid={`${testIdPrefix}-options`}>
            {options.map(opt => (
              <button
                key={opt.id}
                onClick={() => toggle(opt.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-[#F7F7EB] transition-colors text-left ${opt.count === 0 ? 'opacity-40' : ''}`}
                data-testid={`${testIdPrefix}-option-${opt.id}`}
                disabled={opt.count === 0}
              >
                <span className={`w-4 h-4 border flex-shrink-0 flex items-center justify-center transition-colors ${
                  selected.includes(opt.id) ? 'bg-[#000] border-[#000]' : 'border-[#ccc]'
                }`}>
                  {selected.includes(opt.id) && <Check className="w-3 h-3 text-white" />}
                </span>
                {showSwatches && <ColourSwatch colour={opt.name} size={16} />}
                <span className="flex-1 truncate">{opt.name}</span>
                <span className="text-[11px] text-[#999] flex-shrink-0">{opt.count}</span>
              </button>
            ))}
            {options.length === 0 && (
              <p className="px-3 py-3 text-sm text-[#999]">No options</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/* ─── Price range selector ─── */
const PRICE_RANGES = [
  { id: '0-10', label: 'Under £10', min: 0, max: 10 },
  { id: '10-25', label: '£10 – £25', min: 10, max: 25 },
  { id: '25-50', label: '£25 – £50', min: 25, max: 50 },
  { id: '50+', label: '£50+', min: 50, max: Infinity },
];

/* ─── In-stock toggle ─── */
const StockToggle = ({ checked, onChange }) => (
  <label className="flex items-center gap-2.5 cursor-pointer group" data-testid="in-stock-toggle">
    <span className={`w-4 h-4 border flex-shrink-0 flex items-center justify-center transition-colors ${
      checked ? 'bg-[#000] border-[#000]' : 'border-[#ccc] group-hover:border-[#999]'
    }`}>
      {checked && <Check className="w-3 h-3 text-white" />}
    </span>
    <span className="flex items-center gap-1.5 text-sm">
      <Package className="w-3.5 h-3.5 text-[#737373]" />
      In Stock Only
    </span>
    <input type="checkbox" className="sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} />
  </label>
);

/* ─── Active filter chip ─── */
export const FilterChip = ({ label, onRemove, testId, colour }) => (
  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#000] text-white text-xs" data-testid={testId}>
    {colour && <ColourSwatch colour={colour} size={12} className="ring-1 ring-white/30" />}
    {label}
    <button onClick={onRemove} className="hover:text-[#DEA193] transition-colors" aria-label={`Remove ${label} filter`}>
      <X className="w-3 h-3" />
    </button>
  </span>
);

/* ─── Saved Filters (localStorage) ─── */
const SAVED_KEY = 'splitsoul_saved_filters';

const loadSavedFilters = () => {
  try {
    return JSON.parse(localStorage.getItem(SAVED_KEY) || '[]');
  } catch { return []; }
};

const persistSavedFilters = (filters) => {
  localStorage.setItem(SAVED_KEY, JSON.stringify(filters));
};

export const useSavedFilters = () => {
  const [saved, setSaved] = useState(() => loadSavedFilters());

  const save = useCallback((name, filterString) => {
    const next = [...saved, { id: Date.now().toString(), name, params: filterString }];
    setSaved(next);
    persistSavedFilters(next);
    toast.success(`Filter "${name}" saved`);
  }, [saved]);

  const remove = useCallback((id) => {
    const next = saved.filter(s => s.id !== id);
    setSaved(next);
    persistSavedFilters(next);
    toast.success('Filter removed');
  }, [saved]);

  return { saved, save, remove };
};

export const SavedFiltersPills = ({ saved, onApply, onRemove }) => {
  if (!saved?.length) return null;
  return (
    <div className="mb-5" data-testid="saved-filters">
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#999] mb-2 font-medium">Saved Filters</p>
      <div className="flex flex-wrap gap-1.5">
        {saved.map(f => (
          <span key={f.id} className="inline-flex items-center group" data-testid={`saved-filter-${f.id}`}>
            <button
              onClick={() => onApply(f.params)}
              className="px-2.5 py-1.5 bg-[#F7F7EB] border border-[#E5D8D0] text-xs text-[#555] hover:bg-[#E5D8D0] hover:text-[#000] transition-colors flex items-center gap-1.5"
              data-testid={`apply-saved-${f.id}`}
            >
              <Bookmark className="w-3 h-3" />
              {f.name}
            </button>
            <button
              onClick={() => onRemove(f.id)}
              className="px-1 py-1.5 bg-[#F7F7EB] border border-l-0 border-[#E5D8D0] text-[#999] hover:text-red-500 hover:bg-red-50 transition-colors"
              title="Remove saved filter"
              data-testid={`remove-saved-${f.id}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
};

export const SaveFilterButton = ({ currentParams, onSave }) => {
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');

  const handleSave = () => {
    if (!name.trim()) return;
    onSave(name.trim(), currentParams);
    setName('');
    setNaming(false);
  };

  if (naming) {
    return (
      <div className="flex items-center gap-1.5 mt-3" data-testid="save-filter-form">
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSave()}
          placeholder="Filter name..."
          className="flex-1 px-2.5 py-1.5 text-xs border border-[#E5D8D0] focus:outline-none focus:border-[#DEA193]"
          autoFocus
          maxLength={30}
          data-testid="save-filter-input"
        />
        <button
          onClick={handleSave}
          disabled={!name.trim()}
          className="px-2.5 py-1.5 bg-[#000] text-white text-xs disabled:opacity-40"
          data-testid="save-filter-confirm"
        >
          Save
        </button>
        <button onClick={() => { setNaming(false); setName(''); }} className="px-1.5 py-1.5 text-[#999] hover:text-[#000]">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setNaming(true)}
      className="flex items-center gap-1.5 mt-3 text-xs text-[#999] hover:text-[#DEA193] transition-colors"
      data-testid="save-filter-trigger"
    >
      <Bookmark className="w-3.5 h-3.5" />
      Save this filter
    </button>
  );
};

/* ─── Main hook: product filtering engine ─── */
export const useProductFilters = (allProducts, urlFilters = {}) => {
  const filtered = useMemo(() => {
    if (!allProducts?.length) return [];
    let result = [...allProducts];

    if (urlFilters.brands?.length)
      result = result.filter(p => urlFilters.brands.includes(p.brand));
    if (urlFilters.categories?.length)
      result = result.filter(p => urlFilters.categories.includes(p.category));
    if (urlFilters.sizes?.length) {
      result = result.filter(p => {
        return urlFilters.sizes.some(s => {
          if (!p.sizes?.includes(s)) return false;
          if (p.stock_by_size) return (p.stock_by_size[s] || 0) > 0;
          return p.stock > 0;
        });
      });
    }
    if (urlFilters.colors?.length)
      result = result.filter(p => p.colors?.some(c => urlFilters.colors.includes(c)));
    if (urlFilters.priceRanges?.length) {
      const ranges = urlFilters.priceRanges.map(id => PRICE_RANGES.find(r => r.id === id)).filter(Boolean);
      result = result.filter(p => {
        const price = (p.on_sale && p.sale_price) ? p.sale_price : p.price;
        return ranges.some(r => price >= r.min && price < r.max);
      });
    }
    if (urlFilters.inStockOnly)
      result = result.filter(isProductInStock);
    if (urlFilters.onSale)
      result = result.filter(p => p.on_sale && p.sale_price > 0);

    return result;
  }, [allProducts, urlFilters]);

  const filterOptions = useMemo(() => {
    if (!allProducts?.length) return { brands: [], categories: [], sizes: [], colors: [], priceRanges: [] };

    const countFor = (field) => {
      let pool = allProducts;
      if (field !== 'brands' && urlFilters.brands?.length)
        pool = pool.filter(p => urlFilters.brands.includes(p.brand));
      if (field !== 'categories' && urlFilters.categories?.length)
        pool = pool.filter(p => urlFilters.categories.includes(p.category));
      if (field !== 'sizes' && urlFilters.sizes?.length)
        pool = pool.filter(p => urlFilters.sizes.some(s => p.sizes?.includes(s) && (!p.stock_by_size || (p.stock_by_size[s] || 0) > 0)));
      if (field !== 'colors' && urlFilters.colors?.length)
        pool = pool.filter(p => p.colors?.some(c => urlFilters.colors.includes(c)));
      if (field !== 'priceRanges' && urlFilters.priceRanges?.length) {
        const ranges = urlFilters.priceRanges.map(id => PRICE_RANGES.find(r => r.id === id)).filter(Boolean);
        pool = pool.filter(p => { const price = (p.on_sale && p.sale_price) ? p.sale_price : p.price; return ranges.some(r => price >= r.min && price < r.max); });
      }
      if (urlFilters.inStockOnly) pool = pool.filter(isProductInStock);
      if (urlFilters.onSale) pool = pool.filter(p => p.on_sale && p.sale_price > 0);
      return pool;
    };

    const brandPool = countFor('brands');
    const brandMap = {};
    brandPool.forEach(p => { if (p.brand) brandMap[p.brand] = (brandMap[p.brand] || 0) + 1; });
    const brands = Object.entries(brandMap).map(([name, count]) => ({ id: name, name, count })).sort((a, b) => a.name.localeCompare(b.name));

    const catPool = countFor('categories');
    const catMap = {};
    catPool.forEach(p => { if (p.category) catMap[p.category] = (catMap[p.category] || 0) + 1; });
    const categories = Object.entries(catMap).map(([name, count]) => ({ id: name, name, count })).sort((a, b) => a.name.localeCompare(b.name));

    const sizePool = countFor('sizes');
    const sizeMap = {};
    sizePool.forEach(p => {
      (p.sizes || []).forEach(s => {
        const inStock = p.stock_by_size ? (p.stock_by_size[s] || 0) > 0 : p.stock > 0;
        if (inStock) sizeMap[s] = (sizeMap[s] || 0) + 1;
      });
    });
    const sizes = Object.entries(sizeMap).map(([name, count]) => ({ id: name, name, count })).sort((a, b) => a.name.localeCompare(b.name));

    const colorPool = countFor('colors');
    const colorMap = {};
    colorPool.forEach(p => {
      (p.colors || []).forEach(c => { if (c?.trim()) colorMap[c] = (colorMap[c] || 0) + 1; });
    });
    const colors = Object.entries(colorMap).map(([name, count]) => ({ id: name, name, count })).sort((a, b) => b.count - a.count);

    const pricePool = countFor('priceRanges');
    const priceRanges = PRICE_RANGES.map(r => {
      const cnt = pricePool.filter(p => {
        const price = (p.on_sale && p.sale_price) ? p.sale_price : p.price;
        return price >= r.min && price < r.max;
      }).length;
      return { id: r.id, name: r.label, count: cnt };
    });

    return { brands, categories, sizes, colors, priceRanges };
  }, [allProducts, urlFilters]);

  return { filtered, filterOptions, totalProducts: allProducts?.length || 0 };
};

/* ─── Filter Panel (used in sidebar and mobile drawer) ─── */
export const FilterPanel = ({
  filterOptions,
  selectedBrands, onBrandsChange,
  selectedCategories, onCategoriesChange,
  selectedSizes, onSizesChange,
  selectedColors, onColorsChange,
  selectedPriceRanges, onPriceRangesChange,
  inStockOnly, onInStockChange,
}) => {
  return (
    <>
      {filterOptions.categories.length > 0 && (
        <div className="mb-5">
          <FilterDropdown label="Product Type" options={filterOptions.categories} selected={selectedCategories} onChange={onCategoriesChange} testIdPrefix="filter-category" />
        </div>
      )}
      {filterOptions.brands.length > 0 && (
        <div className="mb-5">
          <FilterDropdown label="Brand" options={filterOptions.brands} selected={selectedBrands} onChange={onBrandsChange} testIdPrefix="filter-brand" />
        </div>
      )}
      {filterOptions.sizes.length > 0 && (
        <div className="mb-5">
          <FilterDropdown label="Size" options={filterOptions.sizes} selected={selectedSizes} onChange={onSizesChange} testIdPrefix="filter-size" />
        </div>
      )}
      {filterOptions.colors.length > 0 && (
        <div className="mb-5">
          <FilterDropdown label="Colour" options={filterOptions.colors} selected={selectedColors} onChange={onColorsChange} testIdPrefix="filter-color" showSwatches />
        </div>
      )}
      <div className="mb-5">
        <FilterDropdown label="Price" options={filterOptions.priceRanges} selected={selectedPriceRanges} onChange={onPriceRangesChange} testIdPrefix="filter-price" />
      </div>
      <div className="mb-5">
        <StockToggle checked={inStockOnly} onChange={onInStockChange} />
      </div>
    </>
  );
};
